/*
 * Cart-level protection plan — a single checkbox in the cart footer that adds
 * or removes ONE plan line (a variant of the merchant's chosen
 * `settings.protection_plan_product`, picked in Liquid from the cart's eligible
 * value) tagged with the `_protection_plan_for = cart` line-item property.
 *
 * Checking the box POSTs /cart/add.js for the plan variant. Unchecking POSTs
 * /cart/update.js with the plan line's key (read from `data-plan-key`) set to 0
 * — key-based, like assets/cart-bundle.js, because line INDEXES shift as soon
 * as any other line changes and keys do not.
 *
 * The plan variant depends on the cart's value, so it can go stale whenever the
 * cart changes. Liquid re-renders the toggle on every cart refresh with the
 * variant that now applies; each freshly rendered toggle compares that to the
 * plan line actually in the cart and fixes any difference (see `sync`). Doing
 * this when the element connects, rather than on a cart event, covers every
 * path that re-renders the cart UI (quantity edits, removals, add to cart from
 * a product page, the discount validator).
 *
 * Section re-rendering, event dispatch and the cartUpdate `source` value all
 * mirror assets/cart-bundle.js exactly, for the same reason documented there.
 */
(function () {
  'use strict';

  if (window.__cartProtectionPlanInit) return;
  window.__cartProtectionPlanInit = true;

  // One cart request at a time across all toggles (drawer + cart page).
  let busy = false;
  // Sync attempts already made, so a fix that cannot stick never loops.
  const attempted = new Set();

  // Checkout is held back while the plan is being corrected, so a fast click on
  // Checkout cannot go through with a plan that no longer matches the cart.
  // Only buttons that were enabled are touched, and they are tagged so an empty
  // cart's already-disabled button is never switched on by mistake.
  function lockCheckout(locked) {
    document.querySelectorAll('.cart__checkout-button').forEach((button) => {
      if (locked) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.planLocked = 'true';
        }
      } else if (button.dataset.planLocked) {
        button.disabled = false;
        delete button.dataset.planLocked;
      }
    });
  }

  function cartHost(el) {
    const drawer = el.closest('cart-drawer');
    return el.closest('cart-items')
      || el.closest('cart-drawer-items')
      || (drawer && drawer.querySelector('cart-drawer-items'))
      || document.querySelector('cart-items');
  }

  function sectionsFor(host) {
    return host && typeof host.getSectionsToRender === 'function' ? host.getSectionsToRender() : [];
  }

  function request(url, accept, payload) {
    return fetch(url, { ...fetchConfig(accept), ...{ body: JSON.stringify(payload) } })
      .then((response) => response.json());
  }

  function failed(state) {
    return Boolean(state.errors || state.status);
  }

  function renderSections(host, sections, parsedState) {
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      const html = parsedState.sections && parsedState.sections[section.section];
      if (!container || !html) return;
      const target = container.querySelector(section.selector) || container;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(section.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function showError(toggle, message) {
    let text = toggle.querySelector('.cart-item__protection-plan-error');
    if (!text) {
      text = document.createElement('small');
      text.className = 'cart-item__protection-plan-error';
      text.setAttribute('role', 'alert');
      toggle.appendChild(text);
    }
    text.textContent = message;
  }

  function afterCartUpdate(host, sections, parsedState) {
    renderSections(host, sections, parsedState);

    if (host) host.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    document.dispatchEvent(new CustomEvent('cart:change', {
      detail: { cart: parsedState, sectionId: host ? host.dataset.source : null },
    }));

    // See assets/cart-bundle.js for why this source value is required.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
  }

  // Re-render the cart UI from the cart as it actually is now. Used when a
  // request left the cart changed but its own response can't be trusted to
  // show it: /cart/add.js (no sections) succeeded and the update after it did
  // not, so the page still shows the cart from before the add.
  async function refreshCartUi(host, sections) {
    const ids = sections.map((s) => s.section).join(',');
    const [cart, rendered] = await Promise.all([
      fetch(`${window.routes.cart_url}.js`).then((response) => response.json()),
      fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`).then((response) => response.json()),
    ]);
    afterCartUpdate(host, sections, { ...cart, sections: rendered });
  }

  function planPayload(variantId) {
    return { items: [{ id: variantId, quantity: 1, properties: { _protection_plan_for: 'cart' } }] };
  }

  class ProtectionPlanToggle extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector('input[type="checkbox"]');
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.onChange.bind(this));
      }
    }

    connectedCallback() {
      // Lock Checkout right away when a correction is due, not after the
      // deferred sync starts, so there is no window to click through.
      if (this.pendingSignature()) lockCheckout(true);
      // Deferred so the cart render that created this element has finished.
      setTimeout(() => { if (this.isConnected) this.sync(); }, 0);
    }

    setLoading(loading) {
      busy = loading;
      this.classList.toggle('cart-item__protection-plan--loading', loading);
      lockCheckout(loading);
    }

    // Signature of the correction this toggle needs, or '' when the cart's
    // plan line is already right (or this exact correction was already tried).
    pendingSignature() {
      const planKey = this.dataset.planKey;
      const staleKeys = (this.dataset.staleKeys || '').split(',').filter(Boolean);
      const wrongVariant = Boolean(planKey) && this.dataset.planVariantId !== this.dataset.variantId;
      const wrongQuantity = Boolean(planKey) && this.dataset.planQuantity !== '1';
      if (!staleKeys.length && !wrongVariant && !wrongQuantity) return '';

      const signature = [planKey, this.dataset.variantId, staleKeys.join(), this.dataset.planQuantity].join('|');
      return attempted.has(signature) ? '' : signature;
    }

    onChange(event) {
      event.stopPropagation();
      if (busy) {
        // A request is already in flight; revert the click until it settles.
        this.checkbox.checked = !this.checkbox.checked;
        return;
      }

      if (this.checkbox.checked) this.add();
      else this.remove();
    }

    add() {
      const host = cartHost(this);
      const sections = sectionsFor(host);
      const variantId = this.dataset.variantId;
      if (!variantId) return;

      this.setLoading(true);

      request(window.routes.cart_add_url, 'javascript', {
        ...planPayload(variantId),
        sections: sections.map((s) => s.section),
        sections_url: window.location.pathname,
      })
        .then((parsedState) => {
          if (failed(parsedState)) {
            this.checkbox.checked = false;
            showError(this, parsedState.errors || parsedState.description);
            return;
          }
          afterCartUpdate(host, sections, parsedState);
        })
        .catch(() => {
          this.checkbox.checked = false;
          showError(this, window.cartStrings ? window.cartStrings.error : 'Could not update the cart.');
        })
        .finally(() => this.setLoading(false));
    }

    remove() {
      const host = cartHost(this);
      const sections = sectionsFor(host);
      const planKey = this.dataset.planKey;
      if (!planKey) return;

      this.setLoading(true);

      request(window.routes.cart_update_url, undefined, {
        updates: { [planKey]: 0 },
        sections: sections.map((s) => s.section),
        sections_url: window.location.pathname,
      })
        .then((parsedState) => {
          if (parsedState.errors) {
            this.checkbox.checked = true;
            showError(this, parsedState.errors);
            return;
          }
          afterCartUpdate(host, sections, parsedState);
        })
        .catch(() => {
          this.checkbox.checked = true;
          showError(this, window.cartStrings ? window.cartStrings.error : 'Could not update the cart.');
        })
        .finally(() => this.setLoading(false));
    }

    // Bring the cart's plan line in line with what Liquid says should be there:
    //  - leftover per-item plan lines (from before the plan was cart-level) are
    //    removed — they are invisible in the cart UI but still add to the total;
    //  - the plan is swapped to the variant matching the cart's current value, or
    //    removed when no variant matches any more (cart emptied / only accessories);
    //  - the plan quantity is forced back to 1.
    // Never runs twice for the same state, so a failure cannot loop.
    async sync() {
      if (busy) return;

      const signature = this.pendingSignature();
      if (!signature) return;
      attempted.add(signature);

      const planKey = this.dataset.planKey;
      const wantedVariant = this.dataset.variantId;
      const staleKeys = (this.dataset.staleKeys || '').split(',').filter(Boolean);
      const wrongVariant = Boolean(planKey) && this.dataset.planVariantId !== wantedVariant;
      const wrongQuantity = Boolean(planKey) && this.dataset.planQuantity !== '1';

      const host = cartHost(this);
      const sections = sectionsFor(host);
      const updates = {};
      staleKeys.forEach((key) => { updates[key] = 0; });

      // cartChanged: the new plan is in the cart. completed: the update that
      // drops the old line(s) went through and the UI was re-rendered from it.
      let cartChanged = false;
      let completed = false;

      this.setLoading(true);
      try {
        if (wrongVariant && wantedVariant) {
          // Add the new plan first so the cart is never left without one if
          // the add is refused; the old line is dropped in the update below.
          const added = await request(window.routes.cart_add_url, 'javascript', planPayload(wantedVariant));
          if (failed(added)) return;
          cartChanged = true;
          updates[planKey] = 0;
        } else if (wrongVariant) {
          updates[planKey] = 0;
        } else if (wrongQuantity) {
          updates[planKey] = 1;
        }

        const parsedState = await request(window.routes.cart_update_url, undefined, {
          updates,
          sections: sections.map((s) => s.section),
          sections_url: window.location.pathname,
        });
        if (!parsedState.errors) {
          afterCartUpdate(host, sections, parsedState);
          completed = true;
        }
      } catch (error) {
        // Handled below.
      } finally {
        if (cartChanged && !completed) {
          // The new plan was added but the old one was not removed: the cart now
          // holds both while the page still shows the old state. Show the real
          // cart (the fresh toggle keeps the new plan and clears the old one
          // — see the Liquid) and let this correction be tried again.
          attempted.delete(signature);
          try {
            await refreshCartUi(host, sections);
          } catch (error) {
            // Nothing more to do here; the next page load re-syncs.
          }
        }
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get('protection-plan-toggle')) {
    customElements.define('protection-plan-toggle', ProtectionPlanToggle);
  }
})();
